export default class TabSwitcher {
  constructor({
    findContainer,
    buttonSelector,
    activeClass = 'is-active',
    selectedAttribute = 'aria-selected',
    updateHash = true,
    normalizeTabKey,
    getButtonTabKey,
    validateTabKey,
    onTabActivated,
  } = {}) {
    this.findContainer = typeof findContainer === 'function' ? findContainer : () => null;
    this.buttonSelector = buttonSelector || '';
    this.activeClass = activeClass;
    this.selectedAttribute = selectedAttribute;
    this.updateHash = updateHash;

    this.normalizeTabKey =
      typeof normalizeTabKey === 'function' ? normalizeTabKey : this.defaultNormalizeTabKey;
    this.getButtonTabKeyResolver =
      typeof getButtonTabKey === 'function' ? getButtonTabKey : this.defaultGetButtonTabKey;
    this.validateTabKey = typeof validateTabKey === 'function' ? validateTabKey : null;
    this.onTabActivated = typeof onTabActivated === 'function' ? onTabActivated : null;

    this.container = null;
    this.buttons = [];
    this.buttonHandlers = [];
    this.currentTab = '';
  }

  start() {
    this.container = this.findContainer();
    if (!this.container || !this.buttonSelector) return;

    this.buttons = Array.from(this.container.querySelectorAll(this.buttonSelector));
    if (!this.buttons.length) return;

    const hashTab = window.location.hash ? window.location.hash.replace('#', '') : '';
    const initialTab =
      this.findAvailableTab(hashTab) ||
      this.findActiveTabFromMarkup() ||
      this.getButtonTabKey(this.buttons[0]);

    if (!initialTab) return;

    this.activate(initialTab, false);
    this.bindEvents();
  }

  reset() {
    this.destruct();
    this.start();
  }

  destruct() {
    this.buttonHandlers.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler);
    });
    this.buttonHandlers = [];
    this.currentTab = '';
  }

  bindEvents() {
    this.buttons.forEach((button) => {
      const handler = (event) => {
        event.preventDefault();
        this.activate(this.getButtonTabKey(button), true);
      };

      button.addEventListener('click', handler);
      this.buttonHandlers.push({ button, handler });
    });
  }

  defaultNormalizeTabKey(tabKey) {
    if (tabKey === undefined || tabKey === null) return '';
    return String(tabKey).trim();
  }

  defaultGetButtonTabKey(button) {
    if (!button) return '';
    return button.dataset.tab || button.getAttribute('data-tab') || '';
  }

  getButtonTabKey(button) {
    if (!button) return '';
    return this.normalizeTabKey(this.getButtonTabKeyResolver(button));
  }

  findButtonByTab(tabKey) {
    return this.buttons.find((button) => this.getButtonTabKey(button) === tabKey) || null;
  }

  findAvailableTab(tabKey) {
    const normalizedTabKey = this.normalizeTabKey(tabKey);
    if (!normalizedTabKey) return '';

    const exists = this.buttons.some((button) => this.getButtonTabKey(button) === normalizedTabKey);
    if (!exists) return '';

    if (this.validateTabKey && !this.validateTabKey(normalizedTabKey)) {
      return '';
    }

    return normalizedTabKey;
  }

  findActiveTabFromMarkup() {
    const activeButton = this.buttons.find((button) => button.classList.contains(this.activeClass));
    return this.getButtonTabKey(activeButton);
  }

  activate(tabKey, updateHash) {
    const normalizedTabKey = this.findAvailableTab(tabKey);
    if (!normalizedTabKey || normalizedTabKey === this.currentTab) return;

    this.currentTab = normalizedTabKey;
    const activeButton = this.findButtonByTab(normalizedTabKey);

    this.buttons.forEach((button) => {
      const isActive = this.getButtonTabKey(button) === normalizedTabKey;
      button.classList.toggle(this.activeClass, isActive);

      if (this.selectedAttribute) {
        button.setAttribute(this.selectedAttribute, isActive ? 'true' : 'false');
      }
    });

    if (this.onTabActivated) {
      this.onTabActivated({
        tabKey: normalizedTabKey,
        button: activeButton,
        buttons: this.buttons,
        container: this.container,
      });
    }

    if (updateHash && this.updateHash && window.history && window.history.replaceState) {
      const url = `${window.location.pathname}${window.location.search}#${normalizedTabKey}`;
      window.history.replaceState(null, '', url);
    }

    if (updateHash) {
      window.scrollTo(0, 0);
    }
  }
}
